import { useState, useEffect } from "react"
import { Box, Container, IconButton, Link, makeStyles, Toolbar } from "@material-ui/core"
import { Trans } from "@lingui/macro"
import { RowBox } from "../Boxes"
import { Icon } from "../Icon"
import { NOTICE_SCOPE, NOTICE_SEVERITY, useQueryActiveNotices } from "../../data"

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.text.white,
    backgroundColor: ({ severity }) => {
      switch (severity) {
        case NOTICE_SEVERITY.ERROR:
          return theme.palette.error.main
        case NOTICE_SEVERITY.WARNING:
          return theme.palette.warning.main
        case NOTICE_SEVERITY.INFO:
        default:
          return theme.palette.scheduled.main
      }
    },
    fontWeight: 600,
    height: theme.dimensions.offlineBanner.height,
    minHeight: theme.dimensions.offlineBanner.height,
    position: "sticky",
    top: ({ punchClock }) => {
      let top = theme.dimensions.header.height
      if (punchClock) {
        top += theme.dimensions.punchClock.height
      }
      return top
    },
    zIndex: 999,
    transition: "height 0.6s ease, background-color 0.2s ease"
  },
  message: {
    flex: 1,
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center"
    },
    "& a": {
      color: theme.palette.text.white,
      textDecoration: "underline"
    }
  },
  navButton: {
    color: theme.palette.text.white,
    padding: theme.spacing(0.5)
  },
  container: {
    display: "flex",
    alignItems: "center"
  }
}))

const getSeverityIcon = (severity) => {
  switch (severity) {
    case NOTICE_SEVERITY.ERROR:
      return "error"
    case NOTICE_SEVERITY.WARNING:
      return "warning"
    case NOTICE_SEVERITY.INFO:
    default:
      return "info"
  }
}

const GlobalNoticesBanner = ({ punchClock }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [delayed, setDelayed] = useState(false)

  // Delay the query one tick to avoid a fetch getting in-flight that gets cancelled
  // on a refresh access of the dashboard (while settings are being applied).
  useEffect(() => {
    setTimeout(() => {
      setDelayed(true)
    })
  }, [])

  const { data } = useQueryActiveNotices({
    variables: { filter: { scope: NOTICE_SCOPE.GLOBAL } },
    fetchPolicy: "cache-then-network",
    skip: !delayed
  })

  const notices = data?.activeNotices || []

  // Reset index when notices length changes
  useEffect(() => {
    if (currentIndex >= notices.length && notices.length > 0) {
      setCurrentIndex(0)
    }
  }, [notices.length, currentIndex])

  const classes = useStyles({ punchClock, severity: notices[currentIndex]?.severity })

  const hasNotices = notices.length > 0
  const currentNotice = hasNotices ? notices[currentIndex] : null

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : notices.length - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < notices.length - 1 ? prev + 1 : 0))
  }

  const showNavigation = notices.length > 1

  if (!hasNotices) {
    return null
  }

  return (
    <Toolbar className={classes.root} disableGutters role="alert" aria-label="Global notices banner" aria-live="polite">
      <Container maxWidth={false} className={classes.container}>
        {showNavigation && (
          <IconButton className={classes.navButton} onClick={handlePrev} size="small" aria-label="Previous notice">
            <Icon name="chevron-left" />
          </IconButton>
        )}

        <RowBox className={classes.message}>
          {currentNotice && <Icon name={getSeverityIcon(currentNotice.severity)} />}
          <Box ml={1}>{currentNotice?.message || <>&nbsp;</>}</Box>
          {currentNotice?.externalUrl && (
            <Box ml={1}>
              <Link href={currentNotice.externalUrl} target="_blank" rel="noreferrer">
                <Trans>Read more</Trans>
              </Link>
            </Box>
          )}
        </RowBox>

        {showNavigation && (
          <IconButton className={classes.navButton} onClick={handleNext} size="small" aria-label="Next notice">
            <Icon name="chevron-right" />
          </IconButton>
        )}
      </Container>
    </Toolbar>
  )
}

export { GlobalNoticesBanner }
